import React, { useState } from 'react';
import { api } from '../api/client';
import RichTextEditor from './RichTextEditor';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  mode: 'compose' | 'reply' | 'reply-all' | 'forward';
  extractionId?: string;
  extractionSubject?: string;
  toRecipients?: Array<{ emailAddress: string; name?: string }>;
  ccRecipients?: Array<{ emailAddress: string; name?: string }>;
  senderEmail?: string;
}

const ComposeModal: React.FC<Props> = ({
  isOpen,
  onClose,
  mode,
  extractionId,
  extractionSubject = '',
  toRecipients = [],
  ccRecipients = [],
  senderEmail,
}) => {
  const [to, setTo] = useState(toRecipients.map((r) => r.emailAddress).join('; '));
  const [cc, setCc] = useState(ccRecipients.map((r) => r.emailAddress).join('; '));
  const [subject, setSubject] = useState(
    mode === 'compose'
      ? ''
      : mode === 'forward'
      ? `Fwd: ${extractionSubject}`
      : `Re: ${extractionSubject}`
  );
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!to.trim()) {
      setError('To field is required');
      return;
    }

    setSending(true);
    setError('');

    try {
      if (mode === 'compose') {
        const recipients = to.split(';').map((e) => e.trim()).filter(Boolean);
        const ccList = cc.split(';').map((e) => e.trim()).filter(Boolean);
        await api.sendEmail({
          userEmail: senderEmail || '',
          subject,
          body,
          toRecipients: recipients.map((e) => ({ emailAddress: e })),
          ccRecipients: ccList.map((e) => ({ emailAddress: e })),
        });
      } else if (mode === 'reply') {
        await api.replyToEmail(extractionId || '', { body });
      } else if (mode === 'reply-all') {
        await api.replyAllToEmail(extractionId || '', { body });
      } else if (mode === 'forward') {
        const recipients = to.split(';').map((e) => e.trim()).filter(Boolean);
        await api.forwardEmail(extractionId || '', {
          toRecipients: recipients.map((e) => ({ emailAddress: e })),
          body,
        });
      }

      setTo('');
      setCc('');
      setSubject('');
      setBody('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const title = {
    compose: 'New Message',
    reply: 'Reply',
    'reply-all': 'Reply All',
    forward: 'Forward',
  }[mode];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '700px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,.1)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b', margin: 0 }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#64748b',
              padding: '0',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* To */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
              To{(mode === 'reply' || mode === 'reply-all') && (
                <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: '6px' }}>(set by original email)</span>
              )}
            </label>
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              readOnly={mode === 'reply' || mode === 'reply-all'}
              placeholder="Enter recipient email(s), separated by semicolons"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                transition: 'border-color .2s',
                background: (mode === 'reply' || mode === 'reply-all') ? '#f8fafc' : '#fff',
                color: (mode === 'reply' || mode === 'reply-all') ? '#64748b' : 'inherit',
              }}
              onFocus={(e) => {
                if (mode !== 'reply' && mode !== 'reply-all') e.currentTarget.style.borderColor = '#0078d4';
              }}
              onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e8f0')}
            />
          </div>

          {/* CC */}
          {(mode === 'compose' || mode === 'reply-all') && (
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                CC{mode === 'reply-all' && (
                  <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: '6px' }}>(set by original email)</span>
                )}
              </label>
              <input
                type="text"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                readOnly={mode === 'reply-all'}
                placeholder="Enter CC email(s), separated by semicolons"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  transition: 'border-color .2s',
                  background: mode === 'reply-all' ? '#f8fafc' : '#fff',
                  color: mode === 'reply-all' ? '#64748b' : 'inherit',
                }}
                onFocus={(e) => {
                  if (mode !== 'reply-all') e.currentTarget.style.borderColor = '#0078d4';
                }}
                onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e8f0')}
              />
            </div>
          )}

          {/* Subject */}
          {mode === 'compose' && (
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter subject"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  transition: 'border-color .2s',
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#0078d4')}
                onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e8f0')}
              />
            </div>
          )}

          {/* Body */}
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
              Message {mode === 'reply' || mode === 'reply-all' ? '(Your reply above the original)' : ''}
            </label>
            <RichTextEditor
              value={body}
              onChange={setBody}
              placeholder="Type your message..."
              minHeight={200}
            />
          </div>

          {/* Error */}
          {error && (
            <div
              style={{
                background: '#fee2e2',
                color: '#991b1b',
                padding: '12px',
                borderRadius: '6px',
                fontSize: '12px',
              }}
            >
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onClose}
            disabled={sending}
            style={{
              padding: '8px 16px',
              border: '1px solid #e2e8f0',
              background: '#fff',
              color: '#1e293b',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              transition: 'background .2s',
            }}
            onMouseEnter={(e) => {
              if (!sending) e.currentTarget.style.background = '#f8fafc';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fff';
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            style={{
              padding: '8px 16px',
              background: sending ? '#cbd5e1' : '#0078d4',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: sending ? 'default' : 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              opacity: sending ? 0.65 : 1,
              transition: 'background .2s',
            }}
            onMouseEnter={(e) => {
              if (!sending) e.currentTarget.style.background = '#1084db';
            }}
            onMouseLeave={(e) => {
              if (!sending) e.currentTarget.style.background = '#0078d4';
            }}
          >
            {sending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComposeModal;
